import { isEqual, omit } from 'lodash';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { mapPaginated } from '@sb/feathers-types';
import type { Robot, Unsubscribe } from '@sb/remote-control/types';
import { convertEquipmentResponse } from '@sb/remote-control/util/convertEquipmentResponse';

import { getEquipmentService } from '../equipment/utils';
import { getExternalControlSettingsService } from '../external-control-settings/getExternalControlSettingsService';
import { getSafetySettingsService } from '../safety-settings/utils';
import { cleanQuery } from '../utils';

import { convertRobotResponse, getRobotsService } from './utils';

/**
 * State kinds that transition rapidly during jogging (Idle ↔ RunningAdHocCommand
 * on every jog press/release). These transitions are already delivered to UI
 * components in real-time via the RoutineRunnerHandle WebSocket path, so the
 * Feathers subscription does not need to re-emit for them. Filtering them out
 * avoids an expensive combineLatest → convertRobotResponse → React re-render
 * cascade that blocks the main thread and causes the 3D visualizer animation to
 * skip.
 *
 * Important: other state transitions (RoutineRunning, Failure, etc.) must still
 * propagate through Feathers so that components reading robot.routineRunnerState
 * see the correct state.
 */
const JOG_STATE_KINDS = new Set(['Idle', 'RunningAdHocCommand']);

interface OnGetRobotsArguments {
  robotID?: string;
  isDefault?: true;
  routineID?: string | null;
}

export function onGetRobots(
  { robotID, isDefault, routineID }: OnGetRobotsArguments,
  onSnapshot: (robots: Robot.ConvertedResponse[]) => void,
): Unsubscribe {
  const robot$ = getRobotsService()
    .watch()
    .find({
      query: cleanQuery({
        robotID,
        isDefault,
        latestRoutineID: routineID ?? undefined,
        $sort: { name: 1 },
      }),
    })
    .pipe(
      distinctUntilChanged((prev, curr) => {
        const prevRobots = mapPaginated(prev, (r) => r);
        const currRobots = mapPaginated(curr, (r) => r);

        if (prevRobots.length !== currRobots.length) {
          return false;
        }

        return prevRobots.every((prevBot, i) => {
          const currBot = currRobots[i];

          if (isEqual(prevBot, currBot)) {
            return true;
          }

          // Only suppress emissions where the sole difference is a transition
          // between jog states (Idle ↔ RunningAdHocCommand). All other state
          // changes (RoutineRunning, Failure, etc.) must propagate.
          const prevKind = prevBot.routineRunnerStateKind ?? '';
          const currKind = currBot.routineRunnerStateKind ?? '';

          if (
            JOG_STATE_KINDS.has(prevKind) &&
            JOG_STATE_KINDS.has(currKind) &&
            isEqual(
              omit(prevBot, ['routineRunnerStateKind', 'routineIsPaused']),
              omit(currBot, ['routineRunnerStateKind', 'routineIsPaused']),
            )
          ) {
            return true;
          }

          return false;
        });
      }),
    );

  const inputSafeguardRules$ = getSafetySettingsService()
    .watch()
    .find({ $select: ['robotID', 'inputSafeguardRules'] })
    .pipe(
      map((response) => {
        return mapPaginated(response, (r) => r);
      }),
    );

  const outputSafeguardRules$ = getSafetySettingsService()
    .watch()
    .find({ $select: ['robotID', 'outputSafeguardRules'] })
    .pipe(
      map((response) => {
        return mapPaginated(response, (r) => r);
      }),
    );

  const equipment$ = getEquipmentService()
    .watch()
    .find({ query: { isEnabled: true, $sort: { updatedAt: -1 } } })
    .pipe(
      map((response) => {
        return mapPaginated(response, convertEquipmentResponse);
      }),
    );

  const externalControlSettings$ = getExternalControlSettingsService()
    .watch()
    .find({
      query: { isEnabled: true, isRobotIOEnabled: true },
    })
    .pipe(
      map((response) => {
        return mapPaginated(response, (r) => r);
      }),
    );

  const subscription = combineLatest([
    robot$,
    inputSafeguardRules$,
    outputSafeguardRules$,
    equipment$,
    externalControlSettings$,
  ])
    .pipe(
      map(
        ([
          robotResponse,
          inputSafeguardRules,
          outputSafeguardRules,
          equipment,
          externalControlSettings,
        ]) => {
          const robots = mapPaginated(robotResponse, (robotData) =>
            convertRobotResponse(
              robotData,
              inputSafeguardRules.find((s) => s.robotID === robotData.robotID)
                ?.inputSafeguardRules ?? [],
              outputSafeguardRules.find((s) => s.robotID === robotData.robotID)
                ?.outputSafeguardRules ?? [],
              externalControlSettings.find(
                (s) => s.robotID === robotData.robotID,
              ),

              equipment,
            ),
          );

          return robots;
        },
      ),
    )
    .subscribe(onSnapshot);

  return () => subscription.unsubscribe();
}
